import { useEffect, useMemo, useRef, useState } from 'react';
import {
  SLOTS,
  computeIdeals,
  fmt12,
  fmtHHMM,
  parseHHMM,
  nowEasternHHMM,
} from '@/pages/med/medUtils';

// Parse "YYYY-MM-DD" as a local date (no UTC drift).
function parseIso(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}
const WEEKDAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function prettyDate(iso) {
  const d = parseIso(iso);
  return `${WEEKDAY_LONG[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/**
 * Day row — the visual "day box" containing four medication slots.
 *
 * Props:
 *   day          – { date, morning, afternoon, night, bedtime }
 *   onChange(next)  – patch handler. Receives the FULL next day object.
 *   mode         – 'live'  → today's active row, cascading ON
 *                – 'past'  – muted read-only row (with Edit button on the right)
 *                – 'modal' – full editable view WITHOUT cascade (for past-day edit modal)
 *   onOpenEdit() – (past mode only) called when the Edit button is clicked
 */
export default function MedDayRow({ day, onChange, mode = 'live', onOpenEdit }) {
  const ideals = useMemo(() => computeIdeals(day), [day]);
  const isPastMuted = mode === 'past';

  const toggleCheck = (slotKey) => {
    if (isPastMuted) return;
    const current = day[slotKey] || {};
    const nextChecked = !current.checked;
    let nextTaken = current.taken_time;
    let nextIdeal = current.ideal_time;
    if (nextChecked) {
      // Turning ON — stamp taken_time with current ET if empty, and
      // freeze the ideal at whatever it is right now so subsequent
      // upstream edits don't shift it (used by future charts).
      if (!nextTaken) nextTaken = nowEasternHHMM();
      nextIdeal = ideals[slotKey];
    } else {
      // Turning OFF — clear both.
      nextTaken = null;
      nextIdeal = null;
    }
    onChange({
      ...day,
      [slotKey]: { checked: nextChecked, taken_time: nextTaken, ideal_time: nextIdeal },
    });
  };

  const setTaken = (slotKey, hhmm) => {
    if (isPastMuted) return;
    const current = day[slotKey] || {};
    const wasChecked = !!current.checked;
    const nextChecked = !!hhmm;
    let nextIdeal = current.ideal_time;
    if (nextChecked && !wasChecked) {
      // Setting a taken time on an unchecked slot implicitly checks it,
      // so freeze the ideal at this moment.
      nextIdeal = ideals[slotKey];
    } else if (!nextChecked) {
      nextIdeal = null;
    }
    // If it was already checked, keep the previously-frozen ideal.
    onChange({
      ...day,
      [slotKey]: { checked: nextChecked, taken_time: hhmm || null, ideal_time: nextIdeal },
    });
  };

  return (
    <div
      className={[
        'rounded-lg border-2 bg-white',
        // Today's live row anchors the view with a strong dark outline;
        // past-day muted rows stay visible but recede with a lighter
        // outline so the eye lands on today first.
        isPastMuted ? 'border-slate-300' : 'border-slate-800',
      ].join(' ')}
      data-testid={`med-day-row-${day.date}`}
      data-mode={mode}
    >
      {/* Date header */}
      <div className="flex items-start justify-between px-4 pt-3 pb-2 border-b border-slate-100">
        <div
          className={[
            'text-sm sm:text-base font-bold underline underline-offset-4 decoration-2',
            isPastMuted ? 'text-slate-500' : 'text-slate-900',
          ].join(' ')}
          data-testid={`med-day-header-${day.date}`}
        >
          {prettyDate(day.date)}
        </div>
      </div>

      {/* Slot rows: 2 columns (label+check+taken | ideal) */}
      <div className="divide-y divide-slate-100">
        {SLOTS.map((s) => {
          const slot = day[s.key] || { checked: false, taken_time: null };
          const idealHHMM = ideals[s.key];
          return (
            <div
              key={s.key}
              className={[
                'grid grid-cols-[1fr_auto] gap-3 items-center px-4 py-3',
                isPastMuted ? 'opacity-70' : '',
              ].join(' ')}
              data-testid={`med-slot-${day.date}-${s.key}`}
            >
              {/* Left: checkbox + label + editable taken time */}
              <div className="flex items-center gap-3 min-w-0">
                <input
                  type="checkbox"
                  checked={!!slot.checked}
                  disabled={isPastMuted}
                  onChange={() => toggleCheck(s.key)}
                  className="w-4 h-4 accent-emerald-600 flex-shrink-0"
                  aria-label={`${s.label} taken`}
                  data-testid={`med-check-${day.date}-${s.key}`}
                />
                <div className="min-w-0">
                  <div className={[
                    'text-sm font-medium truncate',
                    isPastMuted ? 'text-slate-600' : 'text-slate-900',
                  ].join(' ')}>
                    {s.label}
                  </div>
                  <TakenTimeInline
                    slot={slot}
                    disabled={isPastMuted}
                    onCommit={(hhmm) => setTaken(s.key, hhmm)}
                    testId={`med-taken-${day.date}-${s.key}`}
                  />
                </div>
              </div>

              {/* Right: ideal time in the "Ideal Time" column */}
              <div
                className={[
                  'text-right tabular-nums text-sm sm:text-base font-semibold',
                  isPastMuted ? 'text-slate-400' : 'text-slate-700',
                ].join(' ')}
                data-testid={`med-ideal-${day.date}-${s.key}`}
              >
                {fmt12(idealHHMM)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Past-day footer: edit button */}
      {isPastMuted && (
        <div className="flex justify-end px-4 py-2 border-t border-slate-100">
          <button
            type="button"
            onClick={onOpenEdit}
            className="text-xs px-3 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-50"
            data-testid={`med-day-edit-${day.date}`}
          >
            Edit
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TakenTimeInline
//
// Displays the taken time under the med label. Click it to swap in a
// tiny time <input>; commits on blur or Enter.
// ---------------------------------------------------------------------------
function TakenTimeInline({ slot, disabled, onCommit, testId }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(slot.taken_time || '');
  const inputRef = useRef(null);

  useEffect(() => {
    setDraft(slot.taken_time || '');
  }, [slot.taken_time]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = () => {
    setEditing(false);
    const parsed = parseHHMM(draft);
    if (parsed == null) {
      // Invalid — revert.
      setDraft(slot.taken_time || '');
      return;
    }
    const normalized = fmtHHMM(parsed);
    if (normalized !== (slot.taken_time || '')) onCommit(normalized);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="time"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') { setDraft(slot.taken_time || ''); setEditing(false); }
        }}
        className="mt-0.5 text-xs px-1 py-0.5 border border-slate-300 rounded outline-none focus:ring-2 focus:ring-emerald-300"
        data-testid={`${testId}-input`}
      />
    );
  }

  if (!slot.taken_time) {
    // Not yet taken. Show a subtle placeholder button only if editable.
    if (disabled) {
      return <div className="text-[11px] text-slate-400">Not taken</div>;
    }
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="mt-0.5 text-[11px] text-slate-400 hover:text-slate-600 underline decoration-dotted"
        data-testid={`${testId}-set`}
      >
        Set time
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => !disabled && setEditing(true)}
      disabled={disabled}
      className={[
        'mt-0.5 text-[11px] font-medium tabular-nums',
        disabled ? 'text-slate-500 cursor-default' : 'text-emerald-700 hover:text-emerald-900 underline decoration-dotted',
      ].join(' ')}
      data-testid={testId}
    >
      Taken {fmt12(slot.taken_time)}
    </button>
  );
}
